// GH #1723. Launch a shadow scan over a whole scan.
//
// Offers configured RunPod strategies and freezes work before acknowledging it.
// GPU cost remains unknown until allocation billing can be attributed.
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useCreate, useNotification } from '@refinedev/core'

import { dataProvider } from '../providers/dataProvider'
import {
  ActiveStrategy,
  RunEstimate,
  ScanWithRuns,
} from '../interfaces/matchRunInterfaces'

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`

// Per-match cost is fractions of a cent; two decimal places rounded every
// strategy to $0.00, which is exactly the comparison this dialog exists to show.
const perMatch = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `$${Number(value).toFixed(4)}`

const duration = (seconds: number | null | undefined) => {
  if (!seconds) return null
  if (seconds < 90) return `${Math.round(seconds)}s`
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`
  return `${(seconds / 3600).toFixed(1)} hours`
}

export default function ShadowRunDialog({
  open,
  onClose,
  scans,
  defaultScan,
  onLaunched,
}: {
  open: boolean
  onClose: () => void
  scans: ScanWithRuns[]
  defaultScan: ScanWithRuns | null
  onLaunched: () => void
}) {
  const notify = useNotification()
  const { mutate: trigger } = useCreate()

  const [strategies, setStrategies] = useState<ActiveStrategy[]>([])
  const [strategyKey, setStrategyKey] = useState('')
  const [scanKey, setScanKey] = useState('')
  const [runs, setRuns] = useState(3)
  const [estimate, setEstimate] = useState<RunEstimate | null>(null)
  const [estimating, setEstimating] = useState(false)
  const estimateSequence = useRef(0)
  const [launching, setLaunching] = useState(false)
  const submission = useRef<{ signature: string; id: string } | null>(null)

  useEffect(() => {
    if (!open) return
    dataProvider.custom!({ url: '/match-runs/strategies', method: 'get' })
      .then((response) => {
        const active = response.data as unknown as ActiveStrategy[]
        setStrategies(active)
        // Open on the default; nothing here is "approved", the bake-off is
        // still running.
        setStrategyKey(
          (active.find((s) => s.is_default) || active[0])?.key || ''
        )
      })
      .catch(() =>
        notify?.open?.({
          type: 'error',
          message: 'Could not load the active strategies',
        })
      )
    // Deliberately NOT depending on `notify`: useNotification() returns a fresh
    // object every render, so including it refetched the strategies on every
    // render, which re-rendered, which refetched - ~27 estimate requests a second
    // and a spinner that never resolved.
  }, [open])

  useEffect(() => {
    if (open && defaultScan) {
      setScanKey(`${defaultScan.need_id}:${defaultScan.batch_number}`)
    }
  }, [open, defaultScan])

  const strategy = strategies.find((s) => s.key === strategyKey) || null
  const [needId, batchNumber] = scanKey.split(':')

  // Depends on the strategy's VALUES, not the object: `strategies.find(...)`
  // returns a fresh identity every render, so depending on it re-fired this effect
  // forever and left the estimate stuck loading.
  const model = strategy?.model
  const prompt = strategy?.prompt

  const loadEstimate = useCallback(async () => {
    const sequence = ++estimateSequence.current
    if (!model || !prompt || !needId || !batchNumber) {
      setEstimate(null)
      setEstimating(false)
      return
    }
    setEstimating(true)
    try {
      const response = await dataProvider.custom!({
        url: '/match-runs/estimate',
        method: 'get',
        query: {
          need_id: Number(needId),
          batch_number: Number(batchNumber),
          model,
          prompt,
          runs,
        },
      })
      if (sequence === estimateSequence.current) setEstimate(response.data as unknown as RunEstimate)
    } catch {
      if (sequence === estimateSequence.current) setEstimate(null)
    } finally {
      if (sequence === estimateSequence.current) setEstimating(false)
    }
  }, [model, prompt, needId, batchNumber, runs])

  useEffect(() => {
    loadEstimate()
  }, [loadEstimate])

  const launch = () => {
    if (!strategy || !needId || !batchNumber) return
    const values = { need_id: Number(needId), batch_number: Number(batchNumber), model: strategy.model, prompt: strategy.prompt, runs, run_critique: false }
    const signature = JSON.stringify(values)
    if (submission.current?.signature !== signature) submission.current = { signature, id: crypto.randomUUID() }
    setLaunching(true)
    trigger(
      {
        resource: 'match-runs/trigger',
        values: { ...values, request_id: submission.current.id },
      },
      {
        onSuccess: () => {
          setLaunching(false)
          submission.current = null
          notify?.open?.({
            type: 'success',
            message: `Queued ${runs} × ${strategy.label}`,
            description:
              'RunPod workers process the queue; results appear as assessments finish.',
          })
          onLaunched()
          onClose()
        },
        onError: () => {
          setLaunching(false)
          notify?.open?.({
            type: 'error',
            message: 'Could not start that run',
          })
        },
      }
    )
  }

  const eta = duration(estimate?.estimated_seconds)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Run a shadow scan</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <FormControl fullWidth size="small">
            <InputLabel id="shadow-scan-label">Scan</InputLabel>
            <Select
              labelId="shadow-scan-label"
              label="Scan"
              value={scans.length ? scanKey : ''}
              onChange={(event) => setScanKey(String(event.target.value))}
            >
              {scans.map((scan) => (
                <MenuItem
                  key={`${scan.need_id}:${scan.batch_number}`}
                  value={`${scan.need_id}:${scan.batch_number}`}
                >
                  {scan.org_name} — batch {scan.batch_number}
                  {scan.run_count
                    ? ` (${scan.run_count} runs)`
                    : ' (no runs yet)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="shadow-strategy-label">Match strategy</InputLabel>
            <Select
              labelId="shadow-strategy-label"
              label="Match strategy"
              value={strategies.length ? strategyKey : ''}
              onChange={(event) => setStrategyKey(String(event.target.value))}
            >
              {strategies.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {strategy?.note ? (
            <Typography variant="caption" color="text.secondary">
              {strategy.note}
            </Typography>
          ) : null}

          <TextField
            size="small"
            type="number"
            label="Runs"
            value={runs}
            onChange={(event) =>
              setRuns(Math.min(5, Math.max(1, Number(event.target.value) || 1)))
            }
            inputProps={{ min: 1, max: 5 }}
            helperText="Repeats of the same strategy. One run cannot show whether a disagreement is real or a re-roll."
          />

          {strategy?.available === false && <Alert severity="warning">No RunPod allocation is configured for this strategy.</Alert>}
          {estimate?.backend === 'runpod' ? (
            <Alert severity="info">{estimate.items} matches × {estimate.runs} runs will be queued on RunPod. GPU rental includes startup, processing and idle time; a per-run cost or start time is not yet available.</Alert>
          ) : estimating ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : estimate && estimate.total_usd !== null ? (
            <Alert
              severity={(estimate.total_usd ?? 0) > 5 ? 'warning' : 'info'}
            >
              <Typography variant="body2">
                <strong>
                  {estimate.is_floor ? 'at least ' : ''}
                  {money(estimate.total_usd)}
                </strong>{' '}
                for {estimate.calls.toLocaleString()} calls ({estimate.items}{' '}
                matches × {estimate.runs} runs)
                {eta ? `, roughly ${eta}` : ''}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
              >
                {perMatch(estimate.per_item_usd)}/match — {estimate.basis}
                {estimate.sample_items
                  ? `, from ${estimate.sample_items.toLocaleString()} previous matches`
                  : ''}
              </Typography>
              {estimate.tokens ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {estimate.tokens.prompt.toLocaleString()} in (
                  {estimate.tokens.cached.toLocaleString()} cached) /{' '}
                  {estimate.tokens.completion.toLocaleString()} out per match
                </Typography>
              ) : null}
            </Alert>
          ) : (
            <Alert severity="warning">
              No cost history and no price for this model, so the spend cannot
              be estimated.
            </Alert>
          )}

          {estimate && !estimate.items ? (
            <Alert severity="error">
              This scan has no opportunities with profile text, so there is
              nothing to run.
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={launch}
          disabled={launching || !strategy || strategy.available === false || estimating || !estimate?.items}
        >
          {launching ? 'Queuing…' : `Queue ${runs} × ${strategy?.label || ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
